import { useParams } from 'react-router-dom';
import './index.less';
import { useContext, useEffect, useRef, useState } from 'react';
import { queryMobjectDetail, queryProjectGlobalQuantities, queryQuantities, queryQuantityCurrentDataListGet, queryQuantityData, queryQuantityGroups } from '@/declare/api/monix';
import dayjs from 'dayjs';
import { Transfer2DisplayScale } from '@/utils/unit';
import LineChart from '@/components/charts/line-chart';
import { ReactComponent as DotSvg } from '../icons/dot.svg';
import { ReactComponent as ActiveDotSvg } from '../icons/active-dot.svg';
import { QuantityLevelTypes } from '..';
import { SystemContext } from '@/App';


export default function MonixCurvePanel() {
    const systemContext = useContext(SystemContext);

    const { monixProjectId } = useParams();
    const [constageList, setConstageList] = useState<any[]>([]);
    const [quantityDataList, setQuantityDataList] = useState<any[]>([]);
    const [activeQuantityId, setActiveQuantityId] = useState<number>(null)
    const [activeQuantity, setActiveQuantity] = useState<any>(null);
    const intervalRef = useRef(null); // 定时器
    const isLoadingRef = useRef(false); // 是否正在加载数据
    const activeMonitorObjectIdRef = useRef(null);

    useEffect(() => {
        activeMonitorObjectIdRef.current = systemContext.activeMonitorObjectId;
    }, [systemContext.activeMonitorObjectId])

    useEffect(() => {
        return () => {
            if (!!intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [])

    useEffect(() => {
        if (activeQuantityId == null || !quantityDataList || quantityDataList.length === 0) {
            setActiveQuantity(null);
            return;
        }
        const activeQuantity = quantityDataList.find(q => q.id === activeQuantityId);
        setActiveQuantity(activeQuantity);
    }, [activeQuantityId, quantityDataList])

    useEffect(() => {
        if (!!intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = null;
        setConstageList([]);
        setQuantityDataList([]);
        setActiveQuantityId(null);
        if (!systemContext.activeMonitorObjectId || !monixProjectId) return;
        generateData();
        intervalRef.current = setInterval(() => {
            generateData(true);
        }, 30 * 1000);
    }, [systemContext.activeMonitorObjectId])

    // 获取数据
    const generateData = async (isInterval = false) => {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;
        let mObjDetailRes;
        if(!isInterval){
            mObjDetailRes = await queryMobjectDetail(+systemContext.activeMonitorObjectId);
        }else{
            mObjDetailRes = await queryMobjectDetail(+activeMonitorObjectIdRef.current);
        }
        const mObject = mObjDetailRes.data;
        const promisesRes = await Promise.all([
            queryQuantityGroups(+monixProjectId, {
                project_id: monixProjectId,
                mobject_id: systemContext.activeMonitorObjectId,
            }),
            queryProjectGlobalQuantities(+monixProjectId)
        ]);
        let quantityGroups = promisesRes[0].data || [];
        quantityGroups = quantityGroups.sort((a, b) => b.level - a.level);
        let projectGQuantities = promisesRes[1].data || [];
        let constages = [...(mObject?.constages || [])];
        constages.sort((a, b) => dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf());

        const queryQuantitiesRes = await queryQuantities(+monixProjectId, { mobject_id: systemContext.activeMonitorObjectId });
        let quantities = queryQuantitiesRes.data || [];
        generateQuantityAndData(projectGQuantities, constages, quantities);
    }

    const generateQuantityAndData = async (projectGQuantities, constages, quantities, isInterval = false) => {
        let promiseList = [];
        quantities = quantities.map(item => Object.assign({}, item));
        quantities.forEach((q) => {
            q.constages = constages;
            let gQuantity = projectGQuantities.find(item => item.type === q.type);
            if (gQuantity) {
                q.chinese_name = gQuantity.chinese_name;
                q.unit_type = gQuantity.unit_type;
                q.precision = gQuantity.precision;
                let scale = Transfer2DisplayScale(q.unit_type);
                q.scale = scale;
                q.valid_data = gQuantity.valid_data;
            }
            let params = {
                start_time: null,
                end_time: null,
                sampling_period: null,
                data_type: 1,
            }
            if (params.start_time == null) {
                params['limit'] = 300;
            }
            else if (params.sampling_period == null) {
                let end = params.end_time;
                if (end == null) {
                    end = new Date().getTime();
                }
                let start = params.start_time;
                // TODO: 计算降采样
                params.sampling_period = calculateDownSample(start, end);
            }
            if (params.sampling_period === -1) {
                params.sampling_period = null;
            }
            let promise = queryQuantityData(q.id, params);
            promiseList.push(promise);
        })
        let qids = quantities.map(item => item.id);
        let queryQuantityCurrentDataPromiseRes = await queryQuantityCurrentDataListGet({
            project_id: monixProjectId,
            quantity_ids: qids,
        })
        let dataList = queryQuantityCurrentDataPromiseRes.data || [];
        dataList.forEach(item => {
            if (!item) {
                isLoadingRef.current = false;
                return;
            }
            let id = +item['id'];
            let value = +item['value'];
            let q = quantities.find(qs => qs.id === id);
            q.current_value = value * q.scale;
            if (q.valid_data != null && Math.abs(q.valid_data) < Math.abs(value)) {
                q.current_grade = -1;
            } else if (!q.is_evaluate) {
                q.current_grade = 0;
            } else {
                q.current_grade = checkQuantityEvaluation(q.warning_limit, value, q.constages);
            }
        })
        let promiseAllRes = await Promise.all(promiseList);
        let data = promiseAllRes.map(item => item.data).filter(item => !!item.id) || [];
        quantities.forEach((q, idx) => {
            let qData = data[idx];
            if (!!qData && !!qData.data) {
                qData.data = qData.data?.map(item => {
                    item.value *= q.scale
                    return item;
                }) || [];
                qData.max = qData.max ? qData.max * q.scale : null;
                qData.min = qData.min ? qData.min * q.scale : null;
                qData.mean = qData.mean ? qData.mean * q.scale : null;
                qData.precision = q.precision;
                qData.unit_type = q.unit_type;
                qData.chinese_name = q.chinese_name;
                qData.warning_limit = q.warning_limit;
                qData.is_evaluate = q.is_evaluate;
                qData.scale = q.scale;
            }
        })
        quantities = quantities.sort((a, b) => b.current_grade - a.current_grade);

        setQuantityDataList(data);

        // 不是定时任务更新才需要设置
        if (!isInterval && data.length > 0) {
            setActiveQuantityId(data[0].id)
        }
        isLoadingRef.current = false;
    }

    const calculateDownSample = (start: number, end: number) => {
        const date1 = dayjs(start);
        const date2 = dayjs(end);
        if (date2.diff(date1, 'M') >= 2) {
            return 86400;
        }
        if (date2.diff(date1, 'M') >= 1) {
            return 43200;
        }
        if (date2.diff(date1, 'd') >= 10) {
            return 3600;
        }
        if (date2.diff(date1, 'd') >= 1) {
            return 300;
        }
        return null;
    }

    const checkQuantityEvaluation = (warningLimits, value: number, constages) => {
        let time = dayjs().valueOf();
        if (constages.length === 0) return 0;
        let stage = constages.find((item) => dayjs(item.start_time).valueOf() <= time && dayjs(item.end_time).valueOf() >= time);
        if (!stage) stage = constages[constages.length - 1];
        let limit = warningLimits.find((item) => item.stage_id === stage.id)?.values;
        if (!limit)
            return 0;
        if (Math.abs(limit['t5']) < 1e-9)
            return 0;
        if (!value)
            return 0;
        if (limit['t5'] <= Math.abs(value)) {
            return 5;
        } else if (limit['t4'] <= Math.abs(value)) {
            return 4;
        } else if (limit['t3'] <= Math.abs(value)) {
            return 3;
        } else if (limit['t2'] <= Math.abs(value)) {
            return 2;
        } else {
            return 1;
        }
    }

    return <div className='monix-curve-panel'>
        <div className="curve-title">
            <span>监测曲线</span>
        </div>
        {systemContext.activeMonitorObjectId != null && <>
            {activeQuantity && <div className='curve-content'>
                {activeQuantity.chinese_name}{activeQuantity.name}
            </div>}
            {activeQuantity && <LineChart height={229} precision={activeQuantity?.precision} constages={constageList} q_data={activeQuantity} />}
            <div className='curve-footer'>
                {quantityDataList.map((q, index) => {
                    return <span key={`curve-footer-dot-${index}`} onClick={() => setActiveQuantityId(q.id)}>
                        {activeQuantityId === q.id && <ActiveDotSvg />}
                        {activeQuantityId !== q.id && <DotSvg />}
                    </span>
                })}
            </div>
        </>}
        {systemContext.activeMonitorObjectId == null && <div className='none-curve'>
            未选择监测对象
        </div>}
    </div>

}